import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

dayjs.extend(utc);
dayjs.extend(timezone);

// Time zones
const timezones = [
  { label: "Browser Time", value: dayjs.tz.guess() },
  { label: "UTC", value: "UTC" },
  { label: "Asia/Kolkata", value: "Asia/Kolkata" },
  { label: "Australia/Perth", value: "Australia/Perth" },
];

// Relative options
const relativeOptions = [
  { label: "Last 5 minutes", value: "5m" },
  { label: "Last 15 minutes", value: "15m" },
  { label: "Last 30 minutes", value: "30m" },
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 3 hours", value: "3h" },
  { label: "Last 6 hours", value: "6h" },
  { label: "Last 12 hours", value: "12h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 2 days", value: "2d" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
];

type TimeRange = {
  from: Date;
  to: Date;
  timezone: string;
};

type TimeRangePopoverProps = {
  onApply: (range: { from: Date; to: Date; timezone: string }) => void;
};

const TimeRangePickerPopover: React.FC<TimeRangePopoverProps> = ({
  onApply,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [mode, setMode] = useState<"relative" | "absolute">("relative");
  const [relative, setRelative] = useState("24h");
  const [relativeLabel, setRelativeLabel] = useState("Last 24 hours");
  const [absoluteFrom, setAbsoluteFrom] = useState(
    dayjs().subtract(6, "hour").format("YYYY-MM-DDTHH:mm")
  );
  const [absoluteTo, setAbsoluteTo] = useState(
    dayjs().format("YYYY-MM-DDTHH:mm")
  );
  const [timezoneValue, setTimezoneValue] = useState(dayjs.tz.guess());
  const [appliedRange, setAppliedRange] = useState<TimeRange | null>(null);

  // Update absolute inputs if relative mode selected
  useEffect(() => {
    if (mode === "relative") {
      const now = dayjs();
      const match = relative.match(/^(\d+)([mhd])$/);
      if (match) {
        const value = parseInt(match[1]);
        const unit =
          match[2] === "m" ? "minute" : match[2] === "h" ? "hour" : "day";
        const from = now.subtract(value, unit);
        setAbsoluteFrom(from.format("YYYY-MM-DDTHH:mm"));
        setAbsoluteTo(now.format("YYYY-MM-DDTHH:mm"));
      }
    }
  }, [relative, mode]);

  const handleApply = () => {
    let fromTime: Dayjs;
    let toTime: Dayjs = dayjs();
    let label: string | undefined;

    if (mode === "relative") {
      const match = relative.match(/^(\d+)([mhd])$/);
      if (match) {
        const value = parseInt(match[1]);
        const unit =
          match[2] === "m" ? "minute" : match[2] === "h" ? "hour" : "day";
        fromTime = toTime.subtract(value, unit);
        label = relativeOptions.find((r) => r.value === relative)?.label;
      } else {
        fromTime = toTime;
      }
    } else {
      fromTime = dayjs(absoluteFrom);
      toTime = dayjs(absoluteTo);
    }

    const tz = timezoneValue === "Browser" ? dayjs.tz.guess() : timezoneValue;

    const result = {
      from: dayjs(fromTime).tz(tz).toDate(),
      to: dayjs(toTime).tz(tz).toDate(),
      timezone: tz,
    };

    setAppliedRange(result);
    if (label) setRelativeLabel(label);

    onApply({ from: result.from, to: result.to, timezone: tz }); // invoke parent callback
    setOpen(false);
  };

  const displayText = appliedRange
    ? mode === "relative"
      ? relativeLabel
      : `${dayjs(appliedRange.from).format("DD-MMM, HH:mm")} – ${dayjs(
          appliedRange.to
        ).format("DD-MMM, HH:mm")}`
    : "Select time range";

  const getOffset = (tz: string) => {
    const offsetMinutes = dayjs().tz(tz).utcOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, "0");
    const minutes = String(abs % 60).padStart(2, "0");
    return `UTC${sign}${hours}:${minutes}`;
  };

  return (
    <Box width={{xs: '100%', md:'auto'}}>
      {/* Clickable Summary Field */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          px: 2,
          py: 1,
          border: "1px solid #ccc",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          width: {xs: '100%', md:270},
          cursor: "pointer",
          bgcolor: "white",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {displayText}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" />
      </Box>

      {/* Popper below the field */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { p:{xs:1, md: 2}, width: 600, height:{xs: 430 , md: 340} },
        }}
      >
        <Paper sx={{ width: "100%", height: "100%", boxShadow: "none" }}>
          <Grid container spacing={2} sx={{ height:{xs: '75%', md: "85%"}, marginLeft: 0 }}>
            {/* Absolute Time */}
            <Grid
              item
              xs={6}
              paddingRight={2}
              paddingLeft={0}
              sx={{ height: "100%" }}
            >
              <Typography fontWeight={600} mb={1}>
                Absolute Time Range
              </Typography>

              <TextField
                type="datetime-local"
                size="small"
                label="From"
                fullWidth
                disabled={mode !== "absolute"}
                value={absoluteFrom}
                onChange={(e) => {
                  setMode("absolute");
                  setAbsoluteFrom(e.target.value);
                }}
                margin="dense"
              />

              <TextField
                type="datetime-local"
                size="small"
                label="To"
                fullWidth
                disabled={mode !== "absolute"}
                value={absoluteTo}
                onChange={(e) => {
                  setMode("absolute");
                  setAbsoluteTo(e.target.value);
                }}
                margin="dense"
              />

              <Button
                variant={mode === "absolute" ? "outlined" : "text"}
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setMode("absolute")}
              >
                Use Absolute
              </Button>
            </Grid>

            {/* Relative Time */}
            <Grid
              item
              xs={6}
              sx={{
                pl: 2,
                pr: 2,
                borderLeft: "1px solid #eee",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                Relative Presets
              </Typography>

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                {relativeOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    fullWidth
                    size="small"
                    variant={
                      mode === "relative" && opt.value === relative
                        ? "outlined"
                        : "text"
                    }
                    sx={{ my: 0.25, textAlign: "left" }}
                    onClick={() => {
                      setMode("relative");
                      setRelative(opt.value);
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Divider and Footer */}
          <Divider sx={{ my: "12px" }} />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{xs: "stretch" ,md:"center"}}
            flexDirection={{xs: 'column', md: 'row'}}
            gap={'1rem'}
          >
            <Box display={"flex"} alignItems={"center"} justifyContent={{xs: 'space-between', md: 'flex-start'}}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="timezone-label">Time zone</InputLabel>
                <Select
                  labelId="timezone-label"
                  value={timezoneValue}
                  label="Time zone"
                  onChange={(e) => setTimezoneValue(e.target.value)}
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Show Offset on the right side */}
              <Typography variant="body2" sx={{ ml: 1, whiteSpace: "nowrap" }}>
                {getOffset(timezoneValue)}
              </Typography>
            </Box>
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default TimeRangePickerPopover;
